<template>
    <div
        :class="{'v-anchor-navbar': true, 'c-anchor-navbar--fixed': isFixed, 'c-anchor-navbar--toggled': isToggled, 'c-anchor-navbar--mobile': isMenuTooBig}"
        v-scroll="handleScrollThrottled">
        <div ref="wrap" class="c-anchor-navbar__wrap" v-show="isMounted">
            <div class="c-anchor-navbar__toggle" v-show="pinnedAnchor !== null && isToggled === false">
                <button class="c-anchor-navbar__trigger" @click="toggle"
                        v-text="pinnedAnchor && pinnedAnchor.name"></button>
            </div>

            <div style="overflow-y: hidden; overflow-x: scroll;" :class="{'c-anchor-navbar__inner': true, 'c-anchor-navbar__inner--toggled': isToggled}">
                <div ref="gRow" class="g-row" style="width: 100%;">
                    <div class="g-col-md-1 g-col-lg-1">
                        <div @click="moveLeft" v-if="useArrows" class="scroll-btn nav-scroll-left-btn"></div>
                    </div>
                    <div ref="navigationWrapper" class="destination-nav-wrapper g-col-md-10 g-col-lg-10">
                        <ul ref="ulLike" class="ul-like" :style="{'left': left + 'px'}" style="position: relative; list-style: none;">
                            <li v-for="(anchor, index) in scrollAnchorsFiltered">
                                <a
                                    href="#"
                                    :class="{'c-anchor-navbar__button': true, 'left-border': index === 0, 'right-border': index+1 === activeAnchors.length && additionalLinks.length === 0}"
                                    role="button"
                                    :key="anchor.id"
                                    @click="onClick(anchor, $event)">
                                    <span v-if="activeAnchors.hasOwnProperty(anchor.id) >= 0" v-text="t(anchor.name)"></span>
                                </a>
                            </li>
                            <li v-for="(point, indexSecond) in additionalLinks">
                                <a
                                    :class="{'c-anchor-navbar__button': true, 'left-border': activeAnchors.length === 0 && indexSecond === 0, 'right-border': indexSecond+1 === additionalLinks.length }"
                                    :href="point.link"
                                >
                                    <span v-text="t(point.title)"></span>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="g-col-md-1 g-col-lg-1">
                        <div @click="moveRight" v-if="useArrows" class="scroll-btn nav-scroll-right-btn"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import minBy from 'lodash/minBy';
import throttle from 'lodash/throttle';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { EventBus } from '../helpers/bus';
import { ScrollAnchor } from '../store/modules/ui';
import BaseComponent from './Base';

const UiStore = namespace('ui');

@Component
export default class AnchorNavbarDestination extends BaseComponent {
    public $refs: {
        wrap: HTMLElement;
        ulLike: HTMLElement;
        gRow: HTMLElement;
        navigationWrapper: HTMLElement;
    };

    @UiStore.State('scrollAnchors') protected readonly scrollAnchors: ScrollAnchor[];
    @Prop(Array) protected readonly additionalLinks: any[];
    @Prop(Array) protected readonly activeAnchors: any[];

    protected handleScrollThrottled = throttle(this.handleScroll, 100, {leading: false});

    protected pageYOffset: number = window.pageYOffset;
    protected navbarYOffset: number = 0;
    protected left: number = 0;
    protected offsetCoeff: number = 100;
    protected isToggled: boolean = false;
    protected isMounted: boolean = false;
    protected isMenuTooBig: boolean = false;
    protected useArrows: boolean = false;
    protected scrollAnchorsFilter: ScrollAnchor[] = [];

    protected body: HTMLElement = document.body;

    protected get isFixed(): boolean {
        return (this.navbarYOffset - this.offsetCoeff) <= this.pageYOffset;
    }

    public onClick(anchor: ScrollAnchor, event: Event): void {
        this.isToggled = false;
        if (anchor.id !== 'description') {
          window.scrollTo({
            top: (anchor.el.elm as HTMLElement).offsetTop - this.offsetCoeff,
            behavior: 'smooth',
          });
        }
        event.preventDefault();
    }

    protected get pinnedAnchor(): ScrollAnchor | null {
        if (this.activeAnchor !== null) {
            return this.activeAnchor;
        }
        if (this.scrollAnchorsFiltered.length > 0) {
            return this.scrollAnchorsFiltered[0];
        }

        return null;
    }

    protected get scrollAnchorsFiltered(): ScrollAnchor[] {
        this.scrollAnchorsFilter = [];
        this.activeAnchors.map((anchor) => {
            this.scrollAnchors.map((item) => {
                if (anchor.id === item.id) {
                    const alter = item;
                    alter.name = anchor.title ? anchor.title : alter.name;
                    this.scrollAnchorsFilter.push(alter);
                }
            });
        });

        return this.scrollAnchorsFilter;
    }

    protected get activeAnchor(): ScrollAnchor | null {
        if (this.scrollAnchorsFiltered.length === 0) {
            return null;
        }
        const prevAnchors = this.scrollAnchorsFiltered.filter((anchor: ScrollAnchor) => {
            return ((anchor.el.elm as HTMLElement).offsetTop - this.offsetCoeff) <= this.pageYOffset;
        });
        return minBy<ScrollAnchor>(prevAnchors, (anchor: ScrollAnchor) => {
            return this.pageYOffset - (anchor.el.elm as HTMLElement).offsetTop - this.offsetCoeff;
        }) || null;
    }

    public isActive(anchor: ScrollAnchor): boolean {
        if (this.activeAnchor === null) {
            return false;
        }
        return anchor.id === this.activeAnchor.id;
    }

    public handleScroll(): void {
        const baseMenuNode = document.getElementById('mobile-menu');
        if (baseMenuNode) {
            const element = baseMenuNode.children && baseMenuNode.children.length ? baseMenuNode.children[0] : null;
            if (element) {
                const mobileMenuElement: HTMLElement = element as HTMLElement;
                if (mobileMenuElement.offsetHeight && mobileMenuElement.offsetHeight > 0) {
                    this.body.classList.remove('has-fixed-anchor-navbar');
                    return;
                }
            }
        }

        this.$set(this, 'pageYOffset', window.pageYOffset);
        if (!this.body) {
            return;
        }
        if (this.isFixed) {
            this.body.classList.add('has-fixed-anchor-navbar');
        } else {
            this.body.classList.remove('has-fixed-anchor-navbar');
        }
    }

    public toggle(): void {
        this.isToggled = !this.isToggled;
    }

    public moveLeft(): void {
        if (this.left + 500 < -500) {
            this.left += 500;
        } else {
            this.left = 0;
        }
    }
    public moveRight(): void {
        if ((this.$refs.ulLike.offsetWidth - this.$refs.navigationWrapper.offsetWidth) + (this.left - 500) > 500) {
            this.left -= 500;
        } else {
            this.left = this.left - 520 - ((this.$refs.ulLike.offsetWidth - this.$refs.navigationWrapper.offsetWidth) + (this.left - 500));
        }
    }

    protected onWindowResize(): void {
        this.navbarYOffset = this.$el.offsetTop + this.$el.offsetHeight;
        this.checkAndCentered();
    }

    protected checkAndCentered(): void {
        if (this.$refs.ulLike.offsetWidth < this.$refs.navigationWrapper.offsetWidth) {
            this.useArrows = false;
            this.left = (this.$refs.navigationWrapper.offsetWidth - this.$refs.ulLike.offsetWidth) / 2;
        } else {
            this.left = 0;
            this.useArrows = true;
        }
    }
    protected mounted(): void {
        this.isMounted = true;
        this.$nextTick(function() {
            this.checkAndCentered();
        });

        this.onWindowResize();
        EventBus.$on(['ready', 'load', 'windowResize', 'orientationChange', 'appMounted'], () => {
            this.onWindowResize();
        });
    }
}
</script>
